import type { ValueOrUpdater } from '@dresscode-frontend/utility-types';
import { isFunction } from '@tanstack/table-core';
import { useCallback, useState } from 'react';

import { waitForPopoverAnimation } from '@/utils/ui';

type Options = {
  /**
   * ダイアログが開く直前に呼ばれるコールバック
   */
  onOpen?: () => void;
  /**
   * ダイアログが開く直後に呼ばれるコールバック
   */
  onVisuallyOpened?: () => void;
  /**
   * ダイアログが閉じる直前に呼ばれるコールバック
   */
  onClose?: () => void;
  /**
   * ダイアログが閉じる直後に呼ばれるコールバック
   */
  onVisuallyClosed?: () => void;
};

/**
 * ポップオーバー系（Dialog等）の開閉状態を管理するフック
 * オプションにコールバックを渡すことで、開閉の前後に処理を挟むことができる
 */
export default function usePopoverState(options?: Options) {
  const [open, setOpen] = useState(false);

  const newSetOpen = useCallback(
    async (value: ValueOrUpdater<boolean>, override?: Options) => {
      const resolved = { ...options, ...override };
      const newValue = isFunction(value) ? value(open) : value;
      if (newValue) {
        resolved.onOpen?.();
        setOpen(newValue);
        await waitForPopoverAnimation();
        resolved.onVisuallyOpened?.();
      } else {
        resolved.onClose?.();
        setOpen(newValue);
        await waitForPopoverAnimation();
        resolved.onVisuallyClosed?.();
      }
    },
    [options, open],
  );

  return [open, newSetOpen] as const;
}
